import { extractErrorMessage } from "../utils/acceptSuggestions";
/**
 * ImprovementActionsPage - Enhanced with PDCA Integration
 * ISO 22301 Clause 10.1-10.2 - Nonconformity, Corrective Action & Continual Improvement
 * 
 * Features:
 * - Consolidated view of all improvement actions from PDCA modules
 * - Auto-generated PACA items with AI suggestions
 * - Pending approval queue for BCM Manager
 * - Integration health score dashboard
 */
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import AISuggestionsBanner from "../components/AISuggestionsBanner";
import { persistAcceptedSuggestions, suggestionName } from "../utils/acceptSuggestions";
import StepDialog from "../components/StepDialog";
import ReadinessBadge from "../components/ReadinessBadge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  TrendingUp,
  Plus,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Target,
  Calendar,
  User,
  FileText,
  RefreshCw,
  Edit,
  Eye,
  Sparkles,
  BarChart3,
  Lightbulb,
  Shield,
  Zap,
  Brain,
  Activity,
  BookOpen,
  ClipboardCheck,
  AlertCircle,
  ThumbsUp,
  ThumbsDown,
  Search,
  Filter,
  Play,
  Link,
  Server,
  Building2,
  GraduationCap,
  Scale,
} from "lucide-react";

// ISO 22301 Improvement Categories
const IMPROVEMENT_CATEGORIES = {
  exercise_deficiency: { name: "Exercise Deficiencies", icon: ClipboardCheck, color: "bg-blue-100 text-blue-700", clause: "8.5" },
  risk_treatment_gap: { name: "Risk Treatment Gaps", icon: Shield, color: "bg-red-100 text-red-700", clause: "8.2.3" },
  training_gap: { name: "Training Gaps", icon: GraduationCap, color: "bg-amber-100 text-amber-700", clause: "7.2" },
  compliance_nonconformity: { name: "Compliance Non-Conformities", icon: Scale, color: "bg-purple-100 text-purple-700", clause: "10.1" },
  plan_review_required: { name: "Plan Review Requirements", icon: FileText, color: "bg-sky-100 text-sky-700", clause: "8.4" },
  incident_lessons: { name: "Incident Lessons Learned", icon: Lightbulb, color: "bg-emerald-100 text-emerald-700", clause: "10.2" },
  audit_finding: { name: "Audit Findings", icon: Search, color: "bg-sky-100 text-sky-700", clause: "9.2" },
  stakeholder_communication: { name: "Stakeholder Communication", icon: User, color: "bg-cyan-100 text-cyan-700", clause: "7.4" },
  asset_dependency: { name: "Asset/Dependency Issues", icon: Server, color: "bg-slate-100 text-slate-700", clause: "8.4.4" },
  documentation_gap: { name: "Documentation Gaps", icon: BookOpen, color: "bg-pink-100 text-pink-700", clause: "7.5" },
  exercise_currency: { name: "Exercise Currency", icon: Clock, color: "bg-red-100 text-red-700", clause: "8.5" },
};

const SEVERITY_COLORS = {
  critical: "bg-red-100 text-red-700 border-red-200",
  major: "bg-sky-100 text-sky-700 border-sky-200",
  minor: "bg-amber-100 text-amber-700 border-amber-200",
  observation: "bg-slate-100 text-slate-700 border-slate-200",
};

const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-700",
  open: "bg-blue-100 text-blue-700",
  in_progress: "bg-amber-100 text-amber-700",
  verification: "bg-purple-100 text-purple-700",
  closed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
};

export default function ImprovementActionsPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [summary, setSummary] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [pacaTitles, setPacaTitles] = useState([]);
  const [categories, setCategories] = useState({});
  const [activeTab, setActiveTab] = useState("overview");
  const [scanning, setScanning] = useState(false);
  const [selectedPaca, setSelectedPaca] = useState(null);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [approvalStep, setApprovalStep] = useState(0);
  const [approvalData, setApprovalData] = useState({
    corrective_action: "",
    preventive_action: "",
    responsible_person: "",
    target_date: "",
    root_cause: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const isBCMAdmin = user?.role === "bcm_manager" || user?.role === "admin";

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [dashboardRes, summaryRes, categoriesRes, pacaRes] = await Promise.all([
        api.get("/pdca-integration/dashboard"),
        api.get("/pdca-integration/summary"),
        api.get("/pdca-integration/categories"),
        api.get("/paca/"),
      ]);
      
      setDashboard(dashboardRes.data);
      setSummary(summaryRes.data);
      setCategories(categoriesRes.data.categories || {});
      setPacaTitles((pacaRes.data || []).map((p) => p.title));
      
      // Fetch pending approvals for BCM Manager
      if (isBCMAdmin) {
        const pendingRes = await api.get("/pdca-integration/pending-approval");
        setPendingApprovals(pendingRes.data.items || []);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load improvement actions data");
    } finally {
      setLoading(false);
    }
  }, [api, isBCMAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Accept AI improvement-action suggestions → create real PACA items via the SAME
  // manual endpoint (POST /paca/ → db.paca_items). Dedupe (case-insensitive title)
  // and outcome reporting follow the shared persistAcceptedSuggestions pattern used
  // by the stakeholder/asset/exercise pages.
  const handleAcceptImprovementSuggestions = async (items) => {
    let existingTitles = [];
    try {
      const res = await api.get("/paca/");
      existingTitles = (res.data || []).map((p) => p.title);
    } catch (e) {
      console.error("Could not load existing improvement actions for dedupe:", e);
    }
    const result = await persistAcceptedSuggestions({
      items,
      existingKeys: existingTitles,
      keyOf: (raw) => suggestionName(raw),
      createOne: async (raw) => {
        const o = raw && typeof raw === "object" ? raw : {};
        const payload = {
          title: suggestionName(raw),
          description: String(o.description || o.rationale || ""),
          provenance: "ai_suggestion", // metadata-only marker; not customer-visible
        };
        await api.post("/paca/", payload);
      },
    });
    await fetchData();
    return result;
  };

  const handleRunScan = async () => {
    setScanning(true);
    try {
      const response = await api.post("/pdca-integration/scan");
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to run PDCA scan"));
    } finally {
      setScanning(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedPaca || !approvalData.responsible_person || !approvalData.target_date) {
      toast.error("Please fill in required fields (Responsible Person and Target Date)");
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/pdca-integration/approve/${selectedPaca.id}`, approvalData);
      toast.success("PACA item approved and activated");
      setShowApprovalDialog(false);
      setSelectedPaca(null);
      setApprovalData({
        corrective_action: "",
        preventive_action: "",
        responsible_person: "",
        target_date: "",
        root_cause: "",
      });
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to approve PACA item"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async (pacaId, reason) => {
    try {
      await api.post(`/pdca-integration/reject/${pacaId}?reason=${encodeURIComponent(reason)}`);
      toast.success("PACA item rejected");
      fetchData();
    } catch (error) {
      toast.error("Failed to reject PACA item");
    }
  };

  const openApprovalDialog = (paca) => {
    setSelectedPaca(paca);
    setApprovalData({
      corrective_action: paca.ai_suggested_corrective_action || "",
      preventive_action: paca.ai_suggested_preventive_action || "",
      responsible_person: "",
      target_date: "",
      root_cause: "",
    });
    setShowApprovalDialog(true);
  };

  const getCategoryInfo = (categoryId) => {
    return IMPROVEMENT_CATEGORIES[categoryId] || { 
      name: categoryId, 
      icon: AlertCircle, 
      color: "bg-slate-100 text-slate-700",
      clause: "10.1"
    };
  };

  const getHealthStatusColor = (score) => {
    if (score >= 80) return "text-emerald-600";
    if (score >= 50) return "text-amber-600";
    return "text-red-600";
  };

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="improvement-actions-page">
      <AISuggestionsBanner module="improvement_actions" onAccept={handleAcceptImprovementSuggestions}
        existingKeys={pacaTitles}
        itemKeyOf={(raw) => suggestionName(raw)} />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-purple-100 text-purple-700">ACT Phase</Badge>
            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">ISO 22301 Clause 10</Badge>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Improvement Actions</h1>
          <p className="text-slate-600 mt-1">
            AI-powered PDCA integration for continual improvement
          </p>
          <div className="mt-2">
            <ReadinessBadge
              total={dashboard?.summary?.total_paca_items || 0}
              complete={dashboard?.summary?.completed_items || 0}
              label="Actions"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          {isBCMAdmin && (
            <Button 
              onClick={handleRunScan}
              disabled={scanning}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="run-scan-btn"
            >
              {scanning ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              Run PDCA Scan
            </Button>
          )}
        </div>
      </div>

      {/* Health Score & Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Health Score Card */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-purple-600" />
              PDCA Health Score
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-4">
              <div className={`text-5xl font-bold ${getHealthStatusColor(summary?.health_score || 0)}`}>
                {Math.round(summary?.health_score || 0)}%
              </div>
              <Badge className={`mt-2 ${
                summary?.integration_status === "healthy" ? "bg-emerald-100 text-emerald-700" :
                summary?.integration_status === "needs_attention" ? "bg-amber-100 text-amber-700" :
                "bg-red-100 text-red-700"
              }`}>
                {summary?.integration_status?.replace("_", " ").toUpperCase() || "UNKNOWN"}
              </Badge>
            </div>
            <div className="space-y-3 mt-4">
              {summary?.metrics && Object.entries(summary.metrics).map(([key, value]) => (
                <div key={key} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600 capitalize">{key.replace(/_/g, " ")}</span>
                    <span className="font-medium">{Math.round(value)}%</span>
                  </div>
                  <Progress value={value} className="h-2" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-600" />
              Integration Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-slate-50 rounded-xl text-center">
                <div className="text-3xl font-bold text-slate-900" data-testid="ia-total-paca-items">{dashboard?.summary?.total_paca_items || 0}</div>
                <div className="text-sm text-slate-600">Total PACA Items</div>
              </div>
              <div className="p-4 bg-amber-50 rounded-xl text-center">
                <div className="text-3xl font-bold text-amber-700">{dashboard?.summary?.pending_approval || 0}</div>
                <div className="text-sm text-amber-600">Pending Approval</div>
              </div>
              <div className="p-4 bg-blue-50 rounded-xl text-center">
                <div className="text-3xl font-bold text-blue-700" data-testid="ia-open-items">{dashboard?.summary?.open_items || 0}</div>
                <div className="text-sm text-blue-600">Open Items</div>
              </div>
              <div className="p-4 bg-red-50 rounded-xl text-center">
                <div className="text-3xl font-bold text-red-700">{dashboard?.summary?.overdue_items || 0}</div>
                <div className="text-sm text-red-600">Overdue</div>
              </div>
              <div className="p-4 bg-purple-50 rounded-xl text-center">
                <div className="text-3xl font-bold text-purple-700">{dashboard?.summary?.critical_items || 0}</div>
                <div className="text-sm text-purple-600">Critical</div>
              </div>
              <div className="p-4 bg-emerald-50 rounded-xl text-center">
                <div className="text-3xl font-bold text-emerald-700">{summary?.counts?.high_risks || 0}</div>
                <div className="text-sm text-emerald-600">High Risks</div>
              </div>
              <div className="p-4 bg-sky-50 rounded-xl text-center">
                <div className="text-3xl font-bold text-sky-700">{dashboard?.summary?.auto_generated || 0}</div>
                <div className="text-sm text-sky-700">Auto-Generated</div>
              </div>
              <div className="p-4 bg-cyan-50 rounded-xl text-center">
                <div className="text-3xl font-bold text-cyan-700">{summary?.counts?.open_findings || 0}</div>
                <div className="text-sm text-cyan-600">Open Findings</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          {isBCMAdmin && pendingApprovals.length > 0 && (
            <TabsTrigger value="pending" className="relative">
              Pending Approval
              <Badge className="ml-2 bg-amber-500 text-white">{pendingApprovals.length}</Badge>
            </TabsTrigger>
          )}
          <TabsTrigger value="by-category">By Category</TabsTrigger>
          <TabsTrigger value="by-source">By Source</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Critical & Overdue Items */}
          {(dashboard?.critical_items?.length > 0 || dashboard?.overdue_items?.length > 0) && (
            <Card className="border-red-200 bg-red-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-800">
                  <AlertTriangle className="w-5 h-5" />
                  Items Requiring Immediate Attention
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboard?.critical_items?.map((item, idx) => (
                    <div key={idx} className="p-3 bg-white rounded-lg border border-red-200 flex items-center justify-between">
                      <div>
                        <div className="font-medium text-slate-900">{item.title}</div>
                        <div className="text-sm text-slate-500">
                          {getCategoryInfo(item.category).name} • {item.source}
                        </div>
                      </div>
                      <Badge className="bg-red-100 text-red-700">Critical</Badge>
                    </div>
                  ))}
                  {dashboard?.overdue_items?.map((item, idx) => (
                    <div key={`overdue-${idx}`} className="p-3 bg-white rounded-lg border border-amber-200 flex items-center justify-between">
                      <div>
                        <div className="font-medium text-slate-900">{item.title}</div>
                        <div className="text-sm text-slate-500">
                          Due: {item.target_date} • {item.source}
                        </div>
                      </div>
                      <Badge className="bg-amber-100 text-amber-700">Overdue</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* High Risks */}
          {dashboard?.high_risks?.length > 0 && (
            <Card className="border-sky-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-sky-700" />
                  High Residual Risks ({">"}9)
                </CardTitle>
                <CardDescription>Risks requiring additional treatment</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {dashboard.high_risks.map((risk, idx) => (
                    <div key={idx} className="p-3 bg-sky-50 rounded-lg border border-sky-200">
                      <div className="font-medium text-slate-900">{risk.threat_name}</div>
                      <div className="text-sm text-slate-500">{risk.process_name}</div>
                      <div className="flex items-center justify-between mt-2">
                        <Badge className="bg-red-100 text-red-700">Risk: {risk.residual_risk}</Badge>
                        <span className="text-xs text-slate-500">{risk.treatment}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* By Status */}
          <Card>
            <CardHeader>
              <CardTitle>PACA Items by Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {dashboard?.by_status && Object.entries(dashboard.by_status).map(([status, count]) => (
                  <div key={status} className={`p-4 rounded-xl text-center ${STATUS_COLORS[status] || "bg-slate-100"}`}>
                    <div className="text-2xl font-bold">{count}</div>
                    <div className="text-sm capitalize">{status.replace("_", " ")}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Approval Tab */}
        {isBCMAdmin && (
          <TabsContent value="pending" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-purple-600" />
                  Auto-Generated PACA Items Awaiting Approval
                </CardTitle>
                <CardDescription>
                  Review AI-generated improvement actions and approve or reject them
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pendingApprovals.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-300" />
                    <p>No pending approvals</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingApprovals.map((paca) => {
                      const catInfo = getCategoryInfo(paca.category);
                      const CatIcon = catInfo.icon;
                      
                      return (
                        <div key={paca.id} className="p-4 border rounded-xl bg-slate-50" data-testid={`pending-paca-${paca.id}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className={`p-2 rounded-lg ${catInfo.color}`}>
                                <CatIcon className="w-5 h-5" />
                              </div>
                              <div>
                                <div className="font-semibold text-slate-900">{paca.title}</div>
                                <div className="text-sm text-slate-500 mt-1">{paca.description?.substring(0, 150)}...</div>
                                <div className="flex items-center gap-2 mt-2">
                                  <Badge variant="outline" className={catInfo.color}>{catInfo.name}</Badge>
                                  <Badge className={SEVERITY_COLORS[paca.severity]}>{paca.severity}</Badge>
                                  <Badge variant="outline">ISO {catInfo.clause}</Badge>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleReject(paca.id, "Not applicable")}
                              >
                                <ThumbsDown className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                              <Button 
                                size="sm"
                                onClick={() => openApprovalDialog(paca)}
                                className="bg-emerald-600 hover:bg-emerald-700"
                              >
                                <ThumbsUp className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                            </div>
                          </div>
                          
                          {/* AI Suggestions Preview */}
                          {(paca.ai_suggested_corrective_action || paca.ai_suggested_preventive_action) && (
                            <div className="mt-4 p-3 bg-sky-50 rounded-lg border border-sky-200">
                              <div className="flex items-center gap-2 text-sky-700 font-medium mb-2">
                                <Sparkles className="w-4 h-4" />
                                AI-Suggested Actions
                              </div>
                              {paca.ai_suggested_corrective_action && (
                                <div className="text-sm text-slate-600 mb-2">
                                  <strong>Corrective:</strong> {paca.ai_suggested_corrective_action.substring(0, 200)}...
                                </div>
                              )}
                              {paca.ai_suggested_preventive_action && (
                                <div className="text-sm text-slate-600">
                                  <strong>Preventive:</strong> {paca.ai_suggested_preventive_action.substring(0, 200)}...
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* By Category Tab */}
        <TabsContent value="by-category" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(IMPROVEMENT_CATEGORIES).map(([key, cat]) => {
              const count = dashboard?.by_category?.[key] || 0;
              const CatIcon = cat.icon;
              
              return (
                <Card key={key} className={count > 0 ? "border-l-4 border-l-purple-500" : ""}>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className={`p-3 rounded-xl ${cat.color}`}>
                        <CatIcon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-slate-900">{cat.name}</div>
                        <div className="text-sm text-slate-500">ISO 22301 Clause {cat.clause}</div>
                      </div>
                      <div className="text-3xl font-bold text-slate-900">{count}</div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* By Source Tab */}
        <TabsContent value="by-source" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>PACA Items by Source</CardTitle>
              <CardDescription>Distribution of improvement actions across PDCA modules</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {dashboard?.by_source && Object.entries(dashboard.by_source).map(([source, count]) => (
                  <div key={source} className="p-4 bg-slate-50 rounded-xl text-center border">
                    <div className="text-2xl font-bold text-slate-900">{count}</div>
                    <div className="text-sm text-slate-500 capitalize">{source === "null" ? "Manual" : source.replace("_", " ")}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Approval Dialog — TurboTax Progressive Disclosure */}
      <StepDialog
        open={showApprovalDialog}
        onOpenChange={(open) => { setShowApprovalDialog(open); if (!open) setApprovalStep(0); }}
        title="Approve PACA Item"
        currentStep={approvalStep}
        onStepChange={setApprovalStep}
        onSubmit={handleApprove}
        onCancel={() => setShowApprovalDialog(false)}
        submitLabel={submitting ? "Processing..." : "Approve & Activate"}
        submitDisabled={submitting || !approvalData.responsible_person || !approvalData.target_date}
        saving={submitting}
        canAdvance={
          approvalStep === 0 ? !!approvalData.responsible_person && !!approvalData.target_date :
          true
        }
        steps={[
          { id: "assignment", title: "Assignment", subtitle: "Who is responsible for this action and when is it due?", isComplete: !!approvalData.responsible_person && !!approvalData.target_date },
          { id: "actions", title: "Actions", subtitle: "Review and edit the AI-suggested corrective and preventive actions.", isComplete: !!approvalData.corrective_action },
          { id: "rootcause", title: "Root Cause", subtitle: "Optionally document the root cause analysis.", isComplete: false },
        ]}
      >
        {/* Step 0: Assignment */}
        {approvalStep === 0 && selectedPaca && (
          <div className="space-y-4" data-testid="step-assignment">
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="font-semibold">{selectedPaca.title}</div>
              <div className="text-sm text-slate-500 mt-1">{selectedPaca.description}</div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Responsible Person *</Label>
              <Input
                value={approvalData.responsible_person}
                onChange={(e) => setApprovalData({...approvalData, responsible_person: e.target.value})}
                placeholder="Assignee name"
                data-testid="responsible-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Target Date *</Label>
              <Input
                type="date"
                value={approvalData.target_date}
                onChange={(e) => setApprovalData({...approvalData, target_date: e.target.value})}
                data-testid="target-date-input"
              />
            </div>
          </div>
        )}

        {/* Step 1: Actions */}
        {approvalStep === 1 && (
          <div className="space-y-4" data-testid="step-actions">
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-sm font-semibold">
                <Sparkles className="w-4 h-4 text-sky-700" />
                Corrective Action (AI Suggested)
              </Label>
              <Textarea
                value={approvalData.corrective_action}
                onChange={(e) => setApprovalData({...approvalData, corrective_action: e.target.value})}
                rows={4}
                className="bg-sky-50 border-sky-200"
                data-testid="corrective-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-sm font-semibold">
                <Sparkles className="w-4 h-4 text-sky-700" />
                Preventive Action (AI Suggested)
              </Label>
              <Textarea
                value={approvalData.preventive_action}
                onChange={(e) => setApprovalData({...approvalData, preventive_action: e.target.value})}
                rows={4}
                className="bg-sky-50 border-sky-200"
                data-testid="preventive-input"
              />
            </div>
          </div>
        )}

        {/* Step 2: Root Cause */}
        {approvalStep === 2 && (
          <div className="space-y-4" data-testid="step-rootcause">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Root Cause (Optional)</Label>
              <Textarea
                value={approvalData.root_cause}
                onChange={(e) => setApprovalData({...approvalData, root_cause: e.target.value})}
                rows={3}
                placeholder="Identify the root cause if known..."
                data-testid="rootcause-input"
              />
            </div>
          </div>
        )}
      </StepDialog>
    </div>
  );
}
